import React, { useEffect, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Button from '@cloudscape-design/components/button';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import Input from '@cloudscape-design/components/input';
import Modal from '@cloudscape-design/components/modal';
import Pagination from '@cloudscape-design/components/pagination';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import Table from '@cloudscape-design/components/table';
import api from '../services/apiService';
import { formatDateDe } from '../utils/date';

const riskLabels = {
  normal: 'Keine Auffälligkeit',
  review: 'Prüfen',
  high: 'Hohe Auffälligkeit',
};

const riskTypes = { normal: 'success', review: 'warning', high: 'error' };

const requestStatusLabels = {
  pending_verification: 'E-Mail-Bestätigung ausstehend',
  distribution_processing: 'Verteilung läuft',
  distribution_failed: 'Verteilung fehlgeschlagen',
  open: 'Offen',
  unmatched: 'Ohne passenden Vermieter',
  partially_fulfilled: 'Teilweise erfüllt',
  fulfilled: 'Erfüllt',
};

function formatDateTime(value) {
  if (!value) return '–';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '–' : new Intl.DateTimeFormat('de-DE', {
    dateStyle: 'medium', timeStyle: 'short', timeZone: 'Europe/Berlin',
  }).format(date);
}

function joinValues(values) {
  return Array.isArray(values) && values.length ? values.join(' · ') : '–';
}

function itemLabel(item) {
  if (item.machine) return `${item.machine.manufacturer} ${item.machine.model}`;
  return item.category?.name || 'Maschinenkategorie';
}

function requestItemsLabel(request) {
  return (request.items || []).map((item) => `${item.quantity} × ${itemLabel(item)}`).join(', ') || '–';
}

function EyeButton({ label, onClick }) {
  return (
    <Button
      variant="inline-icon"
      ariaLabel={label}
      iconSvg={(
        <svg viewBox="0 0 16 16" focusable="false">
          <path d="M1 8s2.5-4 7-4 7 4 7 4-2.5 4-7 4-7-4-7-4Z" />
          <circle cx="8" cy="8" r="2" />
        </svg>
      )}
      onClick={onClick}
    />
  );
}

function Facts({ entries }) {
  return (
    <div className="admin-request-facts">
      {entries.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value || '–'}</strong>
        </div>
      ))}
    </div>
  );
}

function MonitoringDetails({ data, loading, error, onPageChange }) {
  if (loading) return <div className="admin-request-loading">Anfragerprofil wird geladen …</div>;
  if (error) return <Alert type="error">{error}</Alert>;
  if (!data) return null;
  const { profile, stats, risk, variants, linkedIdentities, requestedArticles, requests } = data;

  return (
    <SpaceBetween size="l">
      <Alert type={risk.level === 'high' ? 'error' : risk.level === 'review' ? 'warning' : 'success'} header={riskLabels[risk.level]}>
        {risk.signals.length
          ? <ul className="monitoring-signals">{risk.signals.map((signal) => <li key={signal}>{signal}</li>)}</ul>
          : 'Auf Basis der vorhandenen Daten wurden keine konkreten Auffälligkeiten erkannt.'}
      </Alert>

      <Container header={<Header variant="h2">Identifizierte Anfragerdaten</Header>}>
        <Facts entries={[
          ['Firma', profile.company],
          ['Ansprechpartner', profile.requesterName],
          ['E-Mail', profile.email],
          ['Telefon', profile.phone],
          ['Vervollständigte Firmenanschrift', profile.qualifiedAddress],
          ['Erste Anfrage', formatDateTime(profile.firstRequestAt)],
          ['Letzte Anfrage', formatDateTime(profile.lastRequestAt)],
        ]} />
      </Container>

      <div className="dashboard-metrics">
        <div className="metric"><span>Anfragen gesamt</span><strong>{stats.requestCount}</strong></div>
        <div className="metric"><span>Letzte 24 Stunden</span><strong>{stats.requestsLast24h}</strong></div>
        <div className="metric"><span>Letzte 7 Tage</span><strong>{stats.requestsLast7d}</strong></div>
        <div className="metric"><span>Letzte 30 Tage</span><strong>{stats.requestsLast30d}</strong></div>
        <div className="metric"><span>Vermieterauswahlen</span><strong>{stats.selectionCount}</strong></div>
        <div className="metric"><span>Nicht bestätigt</span><strong>{stats.unverifiedRequestCount}</strong></div>
        <div className="metric"><span>Verknüpfte Identitäten</span><strong>{stats.linkedIdentityCount}</strong></div>
      </div>

      <Container header={<Header variant="h2" description="Abweichende Angaben aus früheren Anfragen und qualifizierten Firmendaten">Identitätsmerkmale</Header>}>
        <SpaceBetween size="m">
          <Facts entries={[
            ['Verwendete E-Mail-Adressen', joinValues(variants.emails)],
            ['Verwendete Namen', joinValues(variants.names)],
            ['Verwendete Firmen', joinValues(variants.companies)],
            ['Verwendete Telefonnummern', joinValues(variants.phones)],
            ['Einsatzorte', joinValues(variants.locations)],
            ['Netzwerk-Fingerabdrücke', joinValues(variants.networkFingerprints)],
          ]} />
          <div className="monitoring-user-agents">
            <span>Browser-/Gerätekennungen</span>
            {variants.userAgents.length
              ? variants.userAgents.map((agent) => <code key={agent}>{agent}</code>)
              : <strong>Für bestehende Altdaten nicht vorhanden.</strong>}
          </div>
        </SpaceBetween>
      </Container>

      <Table
        items={linkedIdentities}
        wrapLines
        header={<Header variant="h2" counter={`(${linkedIdentities.length})`} description="Andere E-Mail-Adressen mit mindestens einem gleichen pseudonymisierten Netzwerk-Fingerabdruck">Möglicherweise verknüpfte Identitäten</Header>}
        empty="Keine weiteren E-Mail-Identitäten aus denselben Netzwerken gefunden."
        columnDefinitions={[
          { id: 'company', header: 'Firma', cell: (item) => item.company || '–' },
          { id: 'name', header: 'Name', cell: (item) => item.requesterName || '–' },
          { id: 'email', header: 'E-Mail', cell: (item) => item.email },
          { id: 'phone', header: 'Telefon', cell: (item) => item.phone || '–' },
          { id: 'requests', header: 'Anfragen', cell: (item) => item.requestCount },
          { id: 'network', header: 'Gemeinsame Netzwerke', cell: (item) => item.sharedFingerprintCount },
          { id: 'last', header: 'Letzte Anfrage', cell: (item) => formatDateTime(item.lastRequestAt) },
        ]}
      />

      <Table
        items={requestedArticles}
        wrapLines
        header={<Header variant="h2" counter={`(${requestedArticles.length})`}>Wie oft wurde was angefragt?</Header>}
        empty="Keine Anfragepositionen vorhanden."
        columnDefinitions={[
          { id: 'article', header: 'Maschine / Kategorie', cell: (item) => item.label },
          { id: 'category', header: 'Kategorie', cell: (item) => item.categoryName || '–' },
          { id: 'requests', header: 'In Anfragen enthalten', cell: (item) => `${item.requestCount} ×` },
          { id: 'quantity', header: 'Gesamtstückzahl', cell: (item) => item.totalQuantity },
        ]}
      />

      <Table
        items={requests.items}
        wrapLines
        header={<Header variant="h2" counter={`(${requests.pagination.totalItems})`}>Vollständige Anfragehistorie</Header>}
        empty="Keine Anfragen vorhanden."
        columnDefinitions={[
          {
            id: 'request',
            header: 'Anfrage',
            cell: (item) => <><strong>{item.reference}</strong><small className="admin-request-subline">{formatDateTime(item.createdAt)}</small></>,
          },
          {
            id: 'identity',
            header: 'Verwendete Angaben',
            cell: (item) => <>{item.company || 'Keine Firma'}<small className="admin-request-subline">{item.requesterName} · {item.phone || 'ohne Telefon'}</small></>,
          },
          { id: 'items', header: 'Angefragt', cell: requestItemsLabel },
          {
            id: 'place',
            header: 'Einsatz und Zeitraum',
            cell: (item) => <>{item.streetAddress ? `${item.streetAddress}, ` : ''}{item.postalCode} {item.city}<small className="admin-request-subline">{formatDateDe(item.startDate)} – {formatDateDe(item.endDate)}</small></>,
          },
          {
            id: 'network',
            header: 'Netzwerk / Gerät',
            cell: (item) => <>{item.requesterIpHash || 'Altdaten'}<small className="admin-request-subline monitoring-agent-short">{item.requesterUserAgent || 'Keine Gerätekennung'}</small></>,
          },
          {
            id: 'result',
            header: 'Ergebnis',
            cell: (item) => <>{requestStatusLabels[item.status] || item.status}<small className="admin-request-subline">E-Mail: {item.emailVerifiedAt ? `bestätigt am ${formatDateTime(item.emailVerifiedAt)}` : 'nicht bestätigt'} · {item.offerCount} Preisinformationen{item.selectedOffer ? ` · ${item.selectedOffer.tenantName} ausgewählt` : ''}</small></>,
          },
          { id: 'message', header: 'Nachricht', cell: (item) => item.message || '–' },
        ]}
        pagination={(
          <Pagination
            currentPageIndex={requests.pagination.page}
            pagesCount={requests.pagination.totalPages}
            onChange={({ detail }) => onPageChange(detail.currentPageIndex)}
          />
        )}
      />
    </SpaceBetween>
  );
}

export default function AdminRequestMonitoringPage() {
  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalItems: 0, totalPages: 1 });
  const [page, setPage] = useState(1);
  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [details, setDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    api.get('/platform/request-monitoring', {
      params: { page, pageSize: 25, search: search || undefined },
    }).then((response) => {
      if (!active) return;
      setItems(response.data.items);
      setPagination(response.data.pagination);
    }).catch((requestError) => {
      if (active) setError(requestError.response?.data?.error || 'Anfrage-Monitoring konnte nicht geladen werden.');
    }).finally(() => {
      if (active) setLoading(false);
    });
    return () => { active = false; };
  }, [page, search]);

  function applySearch() {
    setPage(1);
    setSearch(searchDraft.trim());
  }

  async function loadProfile(email, profilePage = 1) {
    setSelectedEmail(email);
    setDetailsLoading(true);
    setDetailsError('');
    try {
      const response = await api.get('/platform/request-monitoring/profile', {
        params: { email, page: profilePage, pageSize: 25 },
      });
      setDetails(response.data);
    } catch (requestError) {
      setDetailsError(requestError.response?.data?.error || 'Anfragerprofil konnte nicht geladen werden.');
    } finally {
      setDetailsLoading(false);
    }
  }

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Anfragehäufigkeit, Identitätsmerkmale und konkrete Auffälligkeitssignale prüfen">
        Anfrage Monitoring
      </Header>
      <Alert type="info">
        Netzwerkadressen werden nicht im Klartext gespeichert. Der pseudonymisierte Fingerabdruck dient nur zur Korrelation und kann bei gemeinsam genutzten Firmen-, Mobilfunk- oder öffentlichen Netzwerken mehrere unbeteiligte Personen verbinden. Hinweise erfordern daher immer eine manuelle Prüfung.
      </Alert>
      {error && <Alert type="error" dismissible onDismiss={() => setError('')}>{error}</Alert>}

      <div className="admin-request-filters monitoring-filter">
        <Input
          value={searchDraft}
          placeholder="Firma, Name, E-Mail, Telefon, Ort oder PLZ"
          onChange={({ detail }) => setSearchDraft(detail.value)}
          onKeyDown={({ detail }) => { if (detail.key === 'Enter') applySearch(); }}
        />
        <Button iconName="search" onClick={applySearch}>Suchen</Button>
      </div>

      <div className="admin-request-table-width">
        <Table
          loading={loading}
          items={items}
          wrapLines
          header={<Header variant="h2" counter={`(${pagination.totalItems})`}>Identifizierte Anfrager</Header>}
          empty="Keine passenden Anfrager gefunden."
          columnDefinitions={[
            {
              id: 'risk',
              header: 'Hinweis',
              cell: (item) => <StatusIndicator type={riskTypes[item.risk.level]}>{riskLabels[item.risk.level]}</StatusIndicator>,
            },
            { id: 'company', header: 'Firma', cell: (item) => <strong>{item.company || '–'}</strong> },
            {
              id: 'identity',
              header: 'Anfrager',
              cell: (item) => <>{item.requesterName}<small className="admin-request-subline">{item.email}<br />{item.phone || 'Keine Telefonnummer'}</small></>,
            },
            {
              id: 'frequency',
              header: 'Anfragehäufigkeit',
              cell: (item) => <><strong>{item.requestCount} gesamt</strong><small className="admin-request-subline">24 h: {item.requestsLast24h} · 7 Tage: {item.requestsLast7d} · 30 Tage: {item.requestsLast30d}<br />Nicht bestätigt: {item.unverifiedRequestCount}</small></>,
            },
            {
              id: 'period',
              header: 'Erste / letzte Anfrage',
              cell: (item) => <>{formatDateTime(item.firstRequestAt)}<small className="admin-request-subline">zuletzt: {formatDateTime(item.lastRequestAt)}</small></>,
            },
            {
              id: 'correlation',
              header: 'Identifikation',
              cell: (item) => <>{item.ipFingerprintCount} Netzwerk{item.ipFingerprintCount === 1 ? '' : 'e'}<small className="admin-request-subline">{item.linkedIdentityCount} weitere E-Mail-Identitäten</small></>,
            },
            { id: 'selections', header: 'Auswahlen', cell: (item) => item.selectionCount },
            { id: 'action', header: 'Aktion', cell: (item) => <EyeButton label={`Monitoring für ${item.email} öffnen`} onClick={() => loadProfile(item.email)} /> },
          ]}
          pagination={(
            <Pagination
              currentPageIndex={pagination.page}
              pagesCount={pagination.totalPages}
              onChange={({ detail }) => setPage(detail.currentPageIndex)}
            />
          )}
        />
      </div>

      <Modal
        visible={Boolean(selectedEmail)}
        size="max"
        onDismiss={() => { setSelectedEmail(null); setDetails(null); setDetailsError(''); }}
        header={selectedEmail ? `Anfragerprofil: ${selectedEmail}` : 'Anfragerprofil'}
        footer={<Button onClick={() => { setSelectedEmail(null); setDetails(null); }}>Schließen</Button>}
      >
        <MonitoringDetails
          data={details}
          loading={detailsLoading}
          error={detailsError}
          onPageChange={(profilePage) => loadProfile(selectedEmail, profilePage)}
        />
      </Modal>
    </SpaceBetween>
  );
}
